import axios from 'axios';

const apiBaseAuthenticationService = {
    production: 'https://api.parenttown.com/authentication/v1/',
    staging: 'https://dev-api.parenttown.com/authentication/v1/',
    development: 'http://localhost:8081/authentication/v1/',
};

const getAuthenticationServiceApiBase = (env = 'staging') => {
    let res =
        apiBaseAuthenticationService[
            ['production', 'staging', 'development'].indexOf(env) !== -1
                ? env
                : 'staging'
            ];
    res = res.replace(/\/$/, '');
    return res;
};

const CONFIG = {
    env: 'staging', //production|staging|development
    timeout: 0,
};

const COMMON_HEADERS = {};

/**
 *
 * @param {Record<string, any>} config
 * @param {any} user
 * @param {{
 *   method: 'phone'|'email'|'google'|'facebook',
 *   code?: string,
 *   password?: string,
 *   email?: string,
 *   phone?: string
 * }} data
 * @returns {Promise<Record<string,any>>}
 */
export const authSignIn = async (config = CONFIG, user, data = {}) => {
    const instance = axios.create({
        headers: {
            ...COMMON_HEADERS,
            Country: user.country,
            'Device-Type': 'web',
            Lang: user.locale || user['userLocale'],
        },
    });

    try {
        const req = await instance.post(
            `${getAuthenticationServiceApiBase(config.env)}/sign-in`,
            {...data}
        );

        return req.data;
    } catch (e) {
        throw e;
    }
};

/**
 *
 * @param config
 * @param user
 * @param {{
 *   method: 'email'|'password',
 *   password: string,
 *   code: string,
 *   full_name?: string,
 *   email?: string,
 *   phone?: string
 * }} data - Sign-up payload data. Use email and phone depending on the method used.
 * @returns {Promise<*>}
 */
export const authSignUp = async (config = CONFIG, user, data = {}) => {
    const instance = axios.create({
        headers: {
            ...COMMON_HEADERS,
            Country: user.country,
            'Device-Type': 'web',
            Lang: user.locale || user['userLocale'],
        },
    });

    try {
        const req = await instance.post(
            `${getAuthenticationServiceApiBase(config.env)}/sign-up`,
            {...data}
        );

        return req.data;
    } catch (e) {
        throw e;
    }
};

/**
 * @param config
 * @param user
 * @param {{ refresh_token: string }} data
 * @returns {Promise<*>}
 */
export const authSignOut = async (config = CONFIG, user, data = {}) => {
    const instance = axios.create({
        headers: {
            ...COMMON_HEADERS,
            Country: user.country,
            'Device-Type': 'web',
            Lang: user.locale || user['userLocale'],
        },
    });

    try {
        const req = await instance.post(
            `${getAuthenticationServiceApiBase(config.env)}/sign-out`,
            {...data}
        );

        return req.data;
    } catch (e) {
        throw e;
    }
}

/**
 * @param config {Record<string, any>}
 * @param user
 * @param {{session_uid: string; refresh_token: string}} data
 * @returns {Promise<*>}
 */
export const refreshToken = async (config = CONFIG, user, data = {}) => {
    const instance = axios.create({
        headers: {
            ...COMMON_HEADERS,
            Country: user.country,
            'Device-Type': 'web',
            Lang: user.locale || user['userLocale'],
        },
    });

    try {
        const req = await instance.post(
            `${getAuthenticationServiceApiBase(config.env)}/refresh-token`,
            {...data}
        );

        return req.data;
    } catch (e) {
        throw e;
    }
}

/**
 * @param config {Record<string, any>}
 * @param user
 * @param {{email?: string, phone?: string}} data
 * @returns {Promise<*>}
 */
export const authForgotPassword = async (config = CONFIG, user, data = {}) => {
    const instance = axios.create({
        headers: {
            ...COMMON_HEADERS,
            Country: user.country,
            'Device-Type': 'web',
            Lang: user.locale || user['userLocale'],
        },
    });

    try {
        const req = await instance.post(
            `${getAuthenticationServiceApiBase(config.env)}/user-recovery/forgot-password`,
            {...data}
        );

        return req.data;
    } catch (e) {
        throw e;
    }
}

/**
 * @param config {Record<string, any>}
 * @param user
 * @param {{token: string, password: string, password_type?: 'plain'|string}} data
 * @returns {Promise<*>}
 */
export const authResetPassword = async (config = CONFIG, user, data = {}) => {
    const instance = axios.create({
        headers: {
            ...COMMON_HEADERS,
            Country: user.country,
            'Device-Type': 'web',
            Lang: user.locale || user['userLocale'],
        },
    });

    try {
        const req = await instance.post(
            `${getAuthenticationServiceApiBase(config.env)}/user-recovery/reset-password`,
            {...data}
        );

        return req.data;
    } catch (e) {
        throw e;
    }
}

/**
 * @param config {Record<string, any>}
 * @param user
 * @param {{token: string}} data
 * @returns {Promise<*>}
 */
export const authVerifyPasswordResetToken = async (config = CONFIG, user, data = {}) => {
    const instance = axios.create({
        headers: {
            ...COMMON_HEADERS,
            Country: user.country,
            'Device-Type': 'web',
            Lang: user.locale || user['userLocale'],
        },
    });

    try {
        const req = await instance.post(
            `${getAuthenticationServiceApiBase(config.env)}/user-recovery/verify-reset-token`,
            {...data}
        );

        return req.data;
    } catch (e) {
        throw e;
    }
}